import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@linaria/react';
import { formatDistanceToNow, format } from 'date-fns';
import { resizedImage } from 'utils/image.js';
import { COLORS, Icon, Loader, Pill, typographyStyles, fontStacks } from '@livecommerce/design-system';
import { DASHBOARD_PRODUCTS } from 'constants/products.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';

const { getBodyText, getMediumHeadline } = typographyStyles;

const ProductUpdateWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: ${(props) => props.isExpanded ? 'auto' : '300px'};
  position: relative;
  background-color: ${COLORS.light_100};
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ProductLabel = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 16px;

  svg {
    height: 16px;
    width: 16px;
  }

  p {
    margin: auto 0;
    line-height: 16px;
  }
`;

const Title = styled.h2`
  margin: 16px 0 8px 0;
  ${getMediumHeadline(20)};
`;

const ItemAge = styled.p`
  color: ${COLORS.dark_300};
  ${getBodyText(14, 400)};
  line-height: 21px;
  margin: 8px 0 16px;
`;

const ContentWrapper = styled.div`
  flex-grow: 1;
  position: relative;
`;

const TextContentWrapper = styled.div`
  padding: 40px;
  white-space: pre-wrap;
  ${getBodyText(14, 400)};
  line-height: 21px;
`;

const PosterImageWrapper = styled.div`
  flex-shrink: 0;
  height: 100%;
  max-width: 30%;
`;

const PosterImage = styled.img`
  width: 100%;
  max-height: 100%;
`;

const ShowMoreButton = styled.button`
  background: linear-gradient(180deg, transparent 0%, ${COLORS.light_100} 50%, ${COLORS.light_100} 100%);
  width: 100%;
  padding: 40px 0 16px;
  cursor: pointer;
  border: 0;
  ${getBodyText(14)};
  line-height: 21px;
  color: ${COLORS.dark_300};
  position: absolute;
  bottom: 0;

  &:hover {
    color: ${COLORS.dark_500};
  }
`;

const formatDate = (date) => {
  // If more recent than a couple of weeks, show the relative time
  if (Date.now() - date < 14 * 24 * 60 * 60 * 1000) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  return format(date, 'P'); // "Long localized date"
};

export const ProductUpdateItem = ({
  title,
  createdAt,
  text,
  images = [],
  productTag,
  isNew,
  index,
  onLoad = () => {},
}) => {
  const { t } = useLocalization();
  const [ isExpanded, setIsExpanded ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(true);

  // Check if the productTag matches a "dashboard product" which has an icon and a translation label
  const { icon: productIcon, translationLabel } = DASHBOARD_PRODUCTS[productTag] || {};

  // Use the first image as the poster image
  // If there is no text, we stop here and show a loader instead (below).
  // The sequential loading strategy in ProductUpdates.js will remove the text
  // from all items and add it back once the image for the previous item has loaded.
  const posterImgUrl = text?.length && resizedImage(images[0] || null);

  // Show a loader while the image is loading
  const [ isLoadingImage, setIsLoadingImage ] = useState(posterImgUrl);
  useEffect(() => {
    if (posterImgUrl) {
      const img = new Image();
      const onImageLoad = () => setIsLoadingImage(false);
      img.addEventListener('load', onImageLoad);
      img.addEventListener('error', onImageLoad);
      img.src = posterImgUrl;
      return () => img.removeEventListener('load', onImageLoad);
    }
  }, [ posterImgUrl, index ]);

  useEffect(() => setIsLoading(isLoadingImage || !text), [ isLoadingImage, text ]);

  const containerRef = useRef();
  const textRef = useRef();

  // Auto expand items that are shorter than the container or the first item in the list if it's new
  useEffect(() => {
    if (isLoading || isExpanded) return;
    if (isNew && index === 0) {
      setIsExpanded(true);
      return;
    }
    const { height: textHeight } = textRef.current.getBoundingClientRect();
    const { height: containerHeight } = containerRef.current.getBoundingClientRect();
    if (textHeight < containerHeight) {
      setIsExpanded(true);
    }
  }, [ isLoading, isExpanded, index, isNew ]);

  const onClickExpand = useCallback(() => {
    trackingService.trackClick({
      clickId: 'product-updates-modal:expand',
      payload: {
        title,
      },
    });
    setIsExpanded(true);
  }, [title]);

  if (isLoading) {
    return (
      <ProductUpdateWrapper isExpanded={false}>
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      </ProductUpdateWrapper>
    );
  } else if (typeof onLoad === 'function') {
    onLoad();
  }

  return (
    <ProductUpdateWrapper isExpanded={isExpanded}>
      {posterImgUrl && (
        <PosterImageWrapper>
          <PosterImage src={posterImgUrl} alt={title} />
        </PosterImageWrapper>
      )}
      <ContentWrapper ref={containerRef}>
        <TextContentWrapper ref={textRef}>
          {productIcon && (
            <ProductLabel>
              <Icon name={productIcon} />
              <p>{t(translationLabel)}</p>
            </ProductLabel>
          )}
          <Title>{title}</Title>
          {isNew && <Pill backgroundColor={COLORS.orange_400}>{t('productUpdates.newLabel')}</Pill>}
          <ItemAge>{formatDate(createdAt)}</ItemAge>
          <p dangerouslySetInnerHTML={{ __html: text }} />
        </TextContentWrapper>
        {!isExpanded && <ShowMoreButton onClick={onClickExpand}>{t('genericLabels.showMore')}</ShowMoreButton>}
      </ContentWrapper>
    </ProductUpdateWrapper>
  );
};
